import { Sidebar as HaiSidebar, Icon } from '@hai/ui-react';
import React, { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import MakitoLogoFX from 'src/images/makito-fx-logo.svg';
import MakitoLogoX1 from 'src/images/makito-x1-logo.svg';
import MakitoLogoX4 from 'src/images/makito-x4-logo.svg';
import bgMissionCritical from 'src/images/sidebar-bg-mission-critical.jpg';
import bgMK from 'src/images/sidebar-bg.jpg';
import SmallLogoFX from 'src/images/small-logo-fx.svg';
import SmallLogoX1 from 'src/images/small-logo-x1.svg';
import SmallLogoX4 from 'src/images/small-logo-x4.svg';
import systemInfo, { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { translateRoutes } from 'src/js/helper/sidebar-route-helper';
import {
  useFullStory,
  useSendFsAdInsertionStats,
  useSendFsAdminStats,
  useSendFsLogoStats,
  useSendFsMetadataStats,
  useSendFsStats,
} from 'src/js/hook/use-full-story';
import { useIsSaved } from 'src/js/hook/use-is-saved';
import { useSuccessReaction } from 'src/js/hook/use-reaction';
import { useStores } from 'src/js/hook/use-stores';
import { useTask } from 'src/js/hook/use-task';
import { SidebarRoute } from 'src/js/model/sidebar-route';
import { handleLogout } from 'src/js/pages/login/login';
import { setBrightnessContrast } from 'src/js/pages/settings/status/user-settings';
import { log } from 'src/js/service/logger';
import { useWatch, useWatchObject } from 'src/js/store/use-watch';
import { createLogoutTask } from 'src/js/task/session-task';
import { PRODUCT, constant } from 'src/js/constant';
import { infoCenterHelpExists, localHelpExists } from 'src/js/util/online-status-util';
import { useLocalStorage, useReadLocalStorage } from 'usehooks-ts';
import streamModels from '../pages/streaming/stream/stream-store';
import { generateDataAuto } from '../util/automation';
import audioEncoders from '../pages/streaming/audio-encoder/audio-encoder-store';
import videoEncoders from '../pages/streaming/video-encoder/video-encoder-store';
import { watchEvent } from '../events';
import helpStore from '../store/help-store';
import { useMutable } from '../hook/use-mutable';

const sidebarData: (currentPath: string, t: TFunction) => SidebarRoute[] = (
  currentPath = '',
  _t: TFunction,
) => {
  return [
    {
      key: 'dashboard',
      iconName: 'Dashboard',
      title: 'sidebar.dashboard',
      to: '/',
      defaultPath: currentPath,
    },
    {
      key: 'streams',
      iconName: 'Streams',
      title: 'sidebar.streams',
      to: '/streaming',
    },
    {
      key: 'settings',
      iconName: 'Settings',
      title: 'sidebar.settings',
      to: '/settings',
    },
    {
      key: 'security',
      iconName: 'Security',
      title: 'sidebar.security',
      to: '/security',
    },
    {
      key: 'helpCenter',
      iconName: 'HelpCenter',
      title: 'sidebar.help',
      to: '/help',
    },
  ];
};

//const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
//function getSafariVersion() {
//  const match = navigator.userAgent.match(/Version\/(\d+)\./);
//  return match ? parseInt(match[1], 10) : null;
//}
//const safariVersion = getSafariVersion();
//const isSafari20Plus = isSafari && safariVersion && safariVersion >= 20;

export const Sidebar: React.FunctionComponent = () => {
  const logoutTask = useTask(createLogoutTask());
  const { sessionStore } = useStores();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useLocalStorage(
    `${constant.lStorage.sidebarCollapsed}:${sessionStore.username}`,
    false,
  );

  const [isManuallyToggled, setIsManuallyToggled] = useMutable(false); // if the user specifically toggled the sidebareither way dont annoy him with auto-collapsing/expanding

  const brightness = useReadLocalStorage<string>(`${constant.lStorage.brightness}:${sessionStore.username}`) ?? '100';
  const contrast = useReadLocalStorage<string>(`${constant.lStorage.contrast}:${sessionStore.username}`) ?? '100';

  const SIDEBAR_COLLAPSED_BREAKPOINT = 1300;
  useEffect(() => {
    // doc.haivision.com check routine
    infoCenterHelpExists((err) => {
      if (err) {
        localHelpExists((err) => {
          if (!err) {
            helpStore.online = false;
          }
        });
      } else {
        helpStore.online = true;
      }
    });

    let newCollapsedState = collapsed;
    if (window.innerWidth < SIDEBAR_COLLAPSED_BREAKPOINT && !collapsed) {
      // if the window is smaller than 1100px, we always collapse the sidebar
      setCollapsed(true);
      newCollapsedState = true;
    }

    const body = document.getElementsByTagName('body');
    if (body && typeof body[0].classList !== undefined) {
      //if (isSafari20Plus) {
      //  body[0].classList.add('safari-20-plus');
      //}
      if (newCollapsedState) {
        body[0].classList.add('sidebar-collapsed');
      } else {
        body[0].classList.remove('sidebar-collapsed');
      }
    }

    setBrightnessContrast(brightness, contrast);

    const checkViewportSize = () => {
      // console.log('checking viewport size', window.innerWidth);
      if (window.innerWidth >= SIDEBAR_COLLAPSED_BREAKPOINT) {
        // if the window is larger than 1100px, we expand the sidebar
        if (isManuallyToggled() === false) {
          setCollapsed(false);
          if (body && typeof body[0].classList !== undefined) {
            body[0].classList.remove('sidebar-collapsed');
          }
        }
      }
      if (window.innerWidth < SIDEBAR_COLLAPSED_BREAKPOINT) {
        // if the window is smaller than 1100px, we collapse the sidebar

        // console.log('will collapse sidebar due to viewport size');
        if (isManuallyToggled() === false) {
          setCollapsed(true);
          if (body && typeof body[0].classList !== undefined) {
            body[0].classList.add('sidebar-collapsed');
          }
        }
      }
    };

    // Listen for window resize
    window.addEventListener('resize', checkViewportSize);
    return () => {
      window.removeEventListener('resize', checkViewportSize);
    };
  }, []);

  useSuccessReaction(logoutTask, () => {
    handleLogout(false, navigate);
  });

  // signout begins
  const onSignoutProceed = () => {
    logoutTask();
  };

  const onSignoutCancel = () => {
    log.trace('cancelled signout');
  };

  const { check, MyModal } = useIsSaved(onSignoutProceed, onSignoutCancel);
  const signout = () => {
    if (sessionStore.isUser()) {
      logoutTask(); // we dont have permission on presets or isSaved request. No need to check.
      return;
    }
    // check if unsaved preset first
    check();
  };
  // signout ends

  const handleSelect = (path?: string) => {
    if (path !== null || path !== undefined) {
      navigate(path);
    }
  };

  const handleClickUserName = () => {
    navigate('/security/accounts', { state: { id: sessionStore.username } });
  };

  const toggleCollapse = () => {
    const body = document.getElementsByTagName('body');
    if (body && typeof body[0].classList !== undefined) {
      if (collapsed) {
        body[0].classList.remove('sidebar-collapsed');
      } else {
        body[0].classList.add('sidebar-collapsed');
      }
    }
    setCollapsed(!collapsed);
    setIsManuallyToggled(true); // mark that the sidebar was manually toggled
  };

  let defaultActive = location.pathname;
  if (/^\/settings\//.exec(defaultActive)) {
    defaultActive = '/settings';
  } else if (/^\/security\//.exec(defaultActive)) {
    defaultActive = '/security';
  } else if (/^\/dashboard/.exec(defaultActive)) {
    defaultActive = '/';
  } else if (/^\/streaming/.exec(defaultActive)) {
    defaultActive = '/streaming';
  }

  let logo = MakitoLogoX4;
  let smallLogo = SmallLogoX4;
  let bg = bgMK;
  if (constant.product === PRODUCT.FX) {
    logo = MakitoLogoFX;
    smallLogo = SmallLogoFX;
    bg = bgMissionCritical;
  } else if (constant.product === PRODUCT.X1) {
    logo = MakitoLogoX1;
    smallLogo = SmallLogoX1;
    bg = bgMissionCritical;
  }

  const [systemInfoFetched, setSystemInfoFetched] = useState(systemInfo.version !== '');
  const [analytics, setAnalytics] = useState(systemInfoCapabilities.hasAnalytics());
  useFullStory(analytics, systemInfoFetched);
  useWatch(systemInfo, 'analytics', () => setAnalytics(systemInfoCapabilities.hasAnalytics()));
  useWatchObject(systemInfo, () => setSystemInfoFetched(true));

  // FS analytics for audioEncoders, videoEncoders and streams
  const [audioEncodersFetched, setAudioEncodersFetched] = useState(false);
  useWatchObject(audioEncoders, () => setAudioEncodersFetched(true));
  const [videoEncodersFetched, setVideoEncodersFetched] = useState(false);
  useWatchObject(videoEncoders, () => setVideoEncodersFetched(true));
  const [streamsFetched, setStreamsFetched] = useState(false);
  useWatchObject(streamModels, () => setStreamsFetched(true));
  useSendFsStats(analytics, audioEncodersFetched, videoEncodersFetched, streamsFetched);

  // FS analytics for metadata stuff
  const [metadataFetched, setMetadataFetched] = useState(false);
  watchEvent('metadata-fetched', () => {
    setMetadataFetched(true);
  });
  useSendFsMetadataStats(analytics, metadataFetched);

  // FS analytics for adInsertion stuff
  const [adInsertionFetched, setAdInsertionFetched] = useState(false);
  watchEvent('adInsertion-fetched', () => {
    setAdInsertionFetched(true);
  });
  useSendFsAdInsertionStats(analytics, adInsertionFetched);

  // FS analytics for logo stuff
  const [logoFetched, setLogoFetched] = useState(false);
  watchEvent('logo-fetched', () => {
    setLogoFetched(true);
  });
  useSendFsLogoStats(analytics, logoFetched);

  // FS analytics for admin stuff
  const [licensesFetched, setLiensesFetched] = useState(false);
  watchEvent('licenses-fetched', () => {
    setLiensesFetched(true);
  });
  const [certificatesFetched, setCertificatesFetched] = useState(false);
  watchEvent('certificates-fetched', () => {
    setCertificatesFetched(true);
  });
  const [accountFetched, setAccountFetched] = useState();
  watchEvent('account-fetched', (data: any) => {
    setAccountFetched(data.count);
  });
  useSendFsAdminStats(analytics, licensesFetched, certificatesFetched, accountFetched);

  const sidebarMenu = translateRoutes(sidebarData(location.pathname, t), t);

  return (
    <>
      <HaiSidebar
        logo={<Icon src={logo} style={{ height: '70px' }} />}
        /*logoSrc={logo} TODO we should also be able to use this but doesn't work at the moment with sizing */
        logoCollapsedSrc={smallLogo}
        accentColor={constant.productColor}
        backgroundImage={bg}
        menuContent={sidebarMenu}
        userName={sessionStore.username}
        onClickUserName={handleClickUserName}
        defaultActive={defaultActive}
        data-auto={generateDataAuto('sidebar', collapsed ? 'collapsed' : 'expanded')}
        onSelect={handleSelect}
        onSignOut={signout}
        signOutText={t('general.signOut')}
        onCollapse={toggleCollapse}
        collapsed={collapsed}
        deviceSettings={{
          userName: sessionStore.username,
          userRole: sessionStore.getRole(),
          deviceName: systemInfo.deviceName,
          deviceIcon: 'Encoders',
        }}
      />
      <MyModal />
    </>
  );
};
